/**
 * Metadata Page - Hierarchical Taxonomy Repository
 *
 * Manages metadata trees with permission delegation:
 * - SuperAdmin: Can modify all + assign permissions
 * - Admin/OrgAdmin: Can modify assigned + delegate downward
 * - Users: Can only modify what's delegated to them
 */

import { useState, useEffect } from 'react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  ShieldCheckIcon,
  ChatBubbleBottomCenterTextIcon,
  TableCellsIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { PageLoader, Modal, ConfirmDialog } from '@/components/common';
import { metadataApi } from '@/services/api';
import { parseMetadataTreeExcel, downloadMetadataTreeTemplate, type ImpPayload } from '@/utils/metadataTreeImport';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import {
  MetadataDomain,
  MetadataTree,
  MetadataNode,
  MetadataNodeGrant,
  GrantLevel,
  BasePermission,
  buildNodeTree,
  GRANT_LEVEL_LABELS,
} from '@/types/metadata';

// ============================================================================
// ICON MAPPING
// ============================================================================

const ICON_MAP: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'user-group': UserGroupIcon,
  'chat-bubble-left-right': ChatBubbleLeftRightIcon,
  'cog': CogIcon,
  'shield-check': ShieldCheckIcon,
  'chat-bubble-bottom-center-text': ChatBubbleBottomCenterTextIcon,
  'table-cells': TableCellsIcon,
  'cube': CubeIcon,
  'document-duplicate': DocumentDuplicateIcon,
  'folder': FolderIcon,
};

function getIcon(iconName: string): React.ComponentType<React.SVGProps<SVGSVGElement>> {
  return ICON_MAP[iconName] || FolderIcon;
}

// ============================================================================
// PERMISSION BADGES
// ============================================================================

function PermissionBadge({ permission }: { permission: BasePermission }) {
  const config = {
    system: {
      icon: LockClosedIcon,
      label: 'Sistema',
      className: 'bg-gray-100 text-gray-600',
    },
    organization: {
      icon: BuildingOfficeIcon,
      label: 'Organizacion',
      className: 'bg-blue-100 text-blue-600',
    },
    user: {
      icon: UserIcon,
      label: 'Usuario',
      className: 'bg-green-100 text-green-600',
    },
  };

  const { icon: Icon, label, className } = config[permission];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function GrantLevelBadge({ level }: { level: GrantLevel }) {
  const colors: Record<GrantLevel, string> = {
    none: 'bg-gray-100 text-gray-500',
    read: 'bg-blue-100 text-blue-600',
    write: 'bg-green-100 text-green-600',
    admin: 'bg-purple-100 text-purple-600',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[level]}`}>
      <KeyIcon className="w-3 h-3" />
      {GRANT_LEVEL_LABELS[level]}
    </span>
  );
}

// ============================================================================
// TREE NODE COMPONENT
// ============================================================================

interface TreeNodeComponentProps {
  node: MetadataNode;
  level?: number;
  onEdit?: (node: MetadataNode) => void;
  onDelete?: (node: MetadataNode) => void;
  onManagePermissions?: (node: MetadataNode) => void;
  onAddChild?: (node: MetadataNode) => void;
  canAddNodes?: boolean;
}

function TreeNodeComponent({
  node,
  level = 0,
  onEdit,
  onDelete,
  onManagePermissions,
  onAddChild,
  canAddNodes,
}: TreeNodeComponentProps) {
  const [expanded, setExpanded] = useState(level < 2);
  const hasChildren = node.children && node.children.length > 0;
  const nodeCode = (node as { code?: string }).code;

  const canEdit = node.can_edit ?? false;
  const canDelete = node.can_delete ?? false;
  const canDelegate = node.can_delegate ?? false;

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer group ${
          level === 0 ? 'font-medium' : ''
        }`}
        style={{ paddingLeft: `${level * 20 + 12}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          )
        ) : (
          <span className="w-4" />
        )}

        {nodeCode && (
          <span className="text-xs font-mono text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{nodeCode}</span>
        )}
        <span className="flex-1 text-sm text-gray-700">{node.name}</span>

        <PermissionBadge permission={node.base_permission} />

        {node.effective_permission && node.effective_permission !== 'none' && (
          <GrantLevelBadge level={node.effective_permission} />
        )}

        <div className="hidden group-hover:flex items-center gap-1">
          {canAddNodes && onAddChild && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddChild(node);
              }}
              className="p-1 text-gray-400 hover:text-green-600 rounded"
              title="Añadir hijo"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          )}
          {canDelegate && onManagePermissions && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onManagePermissions(node);
              }}
              className="p-1 text-gray-400 hover:text-purple-600 rounded"
              title="Gestionar permisos"
            >
              <KeyIcon className="w-4 h-4" />
            </button>
          )}
          {canEdit && onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(node);
              }}
              className="p-1 text-gray-400 hover:text-vida-600 rounded"
              title="Editar"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          {canDelete && onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node);
              }}
              className="p-1 text-gray-400 hover:text-red-600 rounded"
              title="Eliminar"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {hasChildren && expanded && (
        <div>
          {node.children!.map((child) => (
            <TreeNodeComponent
              key={child._id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onManagePermissions={onManagePermissions}
              onAddChild={onAddChild}
              canAddNodes={canAddNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TREE VIEW COMPONENT
// ============================================================================

interface TreeViewProps {
  tree: MetadataTree;
  nodes: MetadataNode[];
  onEditNode?: (node: MetadataNode) => void;
  onDeleteNode?: (node: MetadataNode) => void;
  onManagePermissions?: (node: MetadataNode) => void;
  onAddNode?: () => void;
  onAddChild?: (node: MetadataNode) => void;
  canAddNodes?: boolean;
}

function TreeView({
  tree,
  nodes,
  onEditNode,
  onDeleteNode,
  onManagePermissions,
  onAddNode,
  onAddChild,
  canAddNodes,
}: TreeViewProps) {
  const treeNodes = buildNodeTree(nodes);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{tree.name}</h3>
            <p className="text-sm text-gray-500">{tree.description}</p>
          </div>
          <PermissionBadge permission={tree.base_permission} />
        </div>
      </div>
      <div className="p-2">
        {treeNodes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No hay nodos en este arbol
          </div>
        ) : (
          treeNodes.map((node) => (
            <TreeNodeComponent
              key={node._id}
              node={node}
              onEdit={onEditNode}
              onDelete={onDeleteNode}
              onManagePermissions={onManagePermissions}
              onAddChild={onAddChild}
              canAddNodes={canAddNodes}
            />
          ))
        )}
        {canAddNodes && onAddNode && (
          <button
            onClick={onAddNode}
            className="flex items-center gap-2 w-full px-4 py-2 mt-2 text-sm text-vida-600 hover:bg-vida-50 rounded-lg transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Añadir nodo raíz
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DOMAIN CARD COMPONENT
// ============================================================================

interface DomainCardProps {
  domain: MetadataDomain;
  onClick: () => void;
}

function DomainCard({ domain, onClick }: DomainCardProps) {
  const Icon = getIcon(domain.icon);

  return (
    <button
      onClick={onClick}
      className="card p-6 text-left hover:shadow-md transition-all hover:border-vida-300 group"
    >
      <div className="flex items-start gap-4">
        <div className="p-3 bg-vida-100 rounded-xl group-hover:bg-vida-200 transition-colors">
          <Icon className="w-6 h-6 text-vida-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 group-hover:text-vida-600 transition-colors">
            {domain.name}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{domain.description}</p>
          <p className="text-xs text-gray-400 mt-2">
            {domain.trees?.length || 0} {(domain.trees?.length || 0) === 1 ? 'arbol' : 'arboles'}
          </p>
        </div>
        <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-vida-500 transition-colors" />
      </div>
    </button>
  );
}

// ============================================================================
// EDIT NODE MODAL
// ============================================================================

interface EditNodeModalProps {
  node: MetadataNode | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; code: string }) => void;
  isSaving: boolean;
}

function EditNodeModal({ node, isOpen, onClose, onSave, isSaving }: EditNodeModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (node) {
      setName(node.name);
      setDescription(node.description || '');
      setCode((node as { code?: string }).code || '');
    }
  }, [node]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, description, code });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Editar: ${node?.name || ''}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Código <span className="text-gray-400 font-normal">(opcional, p.ej. 1.05)</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input w-full font-mono"
            placeholder="1.05"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripcion
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input w-full"
            rows={3}
          />
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// ADD NODE MODAL
// ============================================================================

interface AddNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    id: string;
    name: string;
    description: string;
    code: string;
    base_permission: BasePermission;
  }) => void;
  isSaving: boolean;
  treeName?: string;
  parentName?: string | null;
}

function AddNodeModal({ isOpen, onClose, onSave, isSaving, treeName, parentName }: AddNodeModalProps) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [basePermission, setBasePermission] = useState<BasePermission>('organization');

  useEffect(() => {
    if (isOpen) {
      setId('');
      setName('');
      setDescription('');
      setCode('');
      setBasePermission('organization');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id.trim() || !name.trim()) return;
    onSave({ id: id.trim(), name: name.trim(), description, code: code.trim(), base_permission: basePermission });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={parentName ? `Añadir hijo de "${parentName}"` : `Añadir nodo raíz${treeName ? ` a ${treeName}` : ''}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ID (identificador unico)
          </label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.replace(/[^a-z0-9_-]/g, ''))}
            className="input w-full font-mono"
            placeholder="ej: usuarios_listar"
            required
          />
          <p className="mt-1 text-xs text-gray-500">Solo letras minusculas, numeros, guiones y guiones bajos.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Código <span className="text-gray-400 font-normal">(opcional, p.ej. 1.05)</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input w-full font-mono"
            placeholder="1.05"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input w-full"
            rows={3}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Permiso base</label>
          <select
            value={basePermission}
            onChange={(e) => setBasePermission(e.target.value as BasePermission)}
            className="input w-full"
          >
            <option value="system">Sistema (super_admin)</option>
            <option value="organization">Organizacion (admins de la org)</option>
            <option value="user">Usuario (cualquier user autenticado)</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>
            {isSaving ? 'Creando...' : 'Crear nodo'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// MANAGE PERMISSIONS MODAL
// ============================================================================

interface ManagePermissionsModalProps {
  node: MetadataNode | null;
  isOpen: boolean;
  onClose: () => void;
}

function ManagePermissionsModal({ node, isOpen, onClose }: ManagePermissionsModalProps) {
  const [grants, setGrants] = useState<MetadataNodeGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // New-grant form
  const [granteeType, setGranteeType] = useState<'role' | 'user'>('role');
  const [granteeId, setGranteeId] = useState('');
  const [level, setLevel] = useState<GrantLevel>('read');
  const [cascade, setCascade] = useState(true);

  const loadGrants = async () => {
    if (!node) return;
    setLoading(true);
    try {
      const res = await metadataApi.getGrants('node', node._id);
      setGrants(res.data?.data || []);
    } catch (error) {
      console.error('Error loading grants:', error);
      toast.error('Error al cargar permisos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && node) {
      void loadGrants();
      setGranteeType('role');
      setGranteeId('');
      setLevel('read');
      setCascade(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, node]);

  const handleCreateGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!node || !granteeId.trim()) return;
    setCreating(true);
    try {
      await metadataApi.createGrant({
        grantee_type: granteeType,
        grantee_id: granteeId.trim(),
        target_type: 'node',
        target_id: node._id,
        level,
        cascade,
      });
      toast.success('Permiso concedido');
      setGranteeId('');
      void loadGrants();
    } catch (error: any) {
      console.error('Error creating grant:', error);
      toast.error(error.response?.data?.error || 'Error al conceder permiso');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (grantId: string) => {
    if (!confirm('¿Revocar este permiso?')) return;
    try {
      await metadataApi.revokeGrant(grantId);
      toast.success('Permiso revocado');
      void loadGrants();
    } catch (error: any) {
      console.error('Error revoking grant:', error);
      toast.error(error.response?.data?.error || 'Error al revocar');
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Gestionar permisos: ${node?.name || ''}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* Existing grants list */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Permisos delegados</h4>
          {loading ? (
            <p className="text-sm text-gray-500 py-4">Cargando...</p>
          ) : grants.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 italic">Aun no hay permisos delegados sobre este nodo.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y">
              {grants.map((g) => (
                <div key={g._id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {g.grantee_type === 'role' ? 'Rol' : 'Usuario'}: {g.grantee_name || g.grantee_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      Nivel: <span className="font-mono uppercase">{g.level}</span>
                      {g.cascade && <span className="ml-2 text-vida-600">+ cascada</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRevoke(g._id)}
                    className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >
                    Revocar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New grant form */}
        <form onSubmit={handleCreateGrant} className="border-t border-gray-200 pt-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">Conceder nuevo permiso</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
              <select
                value={granteeType}
                onChange={(e) => setGranteeType(e.target.value as 'role' | 'user')}
                className="input w-full"
              >
                <option value="role">Rol</option>
                <option value="user">Usuario</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nivel</label>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value as GrantLevel)}
                className="input w-full"
              >
                <option value="read">read</option>
                <option value="write">write</option>
                <option value="admin">admin</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {granteeType === 'role' ? 'ID del rol' : 'ID del usuario'}
            </label>
            <input
              type="text"
              value={granteeId}
              onChange={(e) => setGranteeId(e.target.value)}
              className="input w-full font-mono"
              placeholder={granteeType === 'role' ? 'ObjectId del rol' : 'ObjectId del usuario'}
              required
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              className="rounded text-vida-600 focus:ring-vida-500"
            />
            <span>Aplicar a nodos hijos (cascada)</span>
          </label>
          <div className="flex justify-end pt-2">
            <button type="submit" className="btn btn-primary" disabled={creating || !granteeId.trim()}>
              {creating ? 'Concediendo...' : 'Conceder permiso'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// ============================================================================
// IMPORT TREE MODAL
// ============================================================================

interface ImportTreeModalProps {
  onClose: () => void;
  onImported: () => void;
}

function ImportTreeModal({ onClose, onImported }: ImportTreeModalProps) {
  const [payload, setPayload] = useState<ImpPayload | null>(null);
  const [clientStats, setClientStats] = useState<{ padres: number; hijos: number; internas: number; refs: number; hojaLinks: number } | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [preview, setPreview] = useState<any | null>(null); // server dry-run result
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFile = async (file: File) => {
    setBusy(true);
    setPreview(null);
    setPayload(null);
    setFileName(file.name);
    try {
      const { payload: p, stats, warnings: w } = await parseMetadataTreeExcel(file);
      setPayload(p);
      setClientStats(stats);
      setWarnings(w);
      // Server dry-run for accurate association resolution + errors.
      const res = await metadataApi.importTree(p, true);
      setPreview(res.data.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'No se pudo leer el fichero');
      setFileName('');
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    if (!payload) return;
    setImporting(true);
    try {
      const res = await metadataApi.importTree(payload, false);
      const data = res.data.data;
      if (!res.data.success) {
        toast.error('El import terminó con errores. Revisa el resumen.');
        setPreview(data);
      } else {
        toast.success(`Importado: ${data.totalNodes} nodos, ${data.totalAssociations - data.totalUnresolved} asociaciones.`);
        onImported();
        onClose();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const errors: string[] = preview?.errors || [];
  const canImport = !!payload && !busy && errors.length === 0;

  return (
    <Modal open onClose={onClose} title="Importar árbol de metadatos">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Sube un Excel que copie el documento (hojas <b>Config</b> + <b>Árbol</b> con columnas
          Padre / Comercial / Elemento). El sistema deduce el árbol comercial, el interno y las
          asociaciones. Primero verás un resumen; nada se crea hasta que confirmes.
        </p>

        <div className="flex items-center gap-3">
          <button type="button" onClick={() => downloadMetadataTreeTemplate()} className="btn btn-secondary inline-flex items-center gap-2">
            <ArrowDownTrayIcon className="w-4 h-4" /> Descargar plantilla
          </button>
          <label className="btn btn-primary inline-flex items-center gap-2 cursor-pointer">
            <ArrowUpTrayIcon className="w-4 h-4" /> {fileName ? 'Cambiar fichero' : 'Subir Excel'}
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
            />
          </label>
          {fileName && <span className="text-xs text-gray-500 truncate">{fileName}</span>}
        </div>

        {busy && <p className="text-sm text-gray-500">Analizando…</p>}

        {preview && clientStats && (
          <div className="border border-gray-200 rounded-lg p-4 space-y-3 text-sm">
            <div className="font-semibold text-gray-700">
              Resumen (dry-run){preview.orgSlug ? ` · org: ${preview.orgSlug}` : ''}
            </div>
            {!preview.orgId && (
              <p className="text-red-600">⚠️ Organización no encontrada para org_slug «{preview.orgSlug}».</p>
            )}
            <div className="grid grid-cols-2 gap-2 text-gray-600">
              <div>Padres comerciales: <b>{clientStats.padres}</b></div>
              <div>Subcategorías (X.YY): <b>{clientStats.hijos}</b></div>
              <div>Categorías internas: <b>{clientStats.internas}</b></div>
              <div>Asociaciones: <b>{preview.totalAssociations}</b></div>
            </div>
            {preview.trees?.map((t: any) => (
              <div key={t.treeId} className="text-gray-600">
                🌳 <b>{t.treeName}</b>: {t.nodes} nodos, {t.associations} asociaciones
                {t.unresolved?.length ? <span className="text-amber-600"> ({t.unresolved.length} sin resolver)</span> : null}
              </div>
            ))}
            {preview.totalUnresolved > 0 && (
              <details className="text-amber-700">
                <summary className="cursor-pointer">Ver {preview.totalUnresolved} asociaciones sin resolver</summary>
                <ul className="list-disc pl-5 mt-1 max-h-32 overflow-y-auto">
                  {preview.trees.flatMap((t: any) => t.unresolved).slice(0, 100).map((u: string, i: number) => (
                    <li key={i}>{u}</li>
                  ))}
                </ul>
              </details>
            )}
            {errors.length > 0 && (
              <div className="text-red-600">
                <div className="font-semibold">Errores (impiden importar):</div>
                <ul className="list-disc pl-5">{errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            {warnings.length > 0 && (
              <details className="text-gray-500">
                <summary className="cursor-pointer">Avisos del fichero ({warnings.length})</summary>
                <ul className="list-disc pl-5 mt-1 max-h-32 overflow-y-auto">
                  {warnings.slice(0, 100).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button type="button" onClick={handleImport} disabled={!canImport || importing} className="btn btn-primary">
            {importing ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// CREATE TREE MODAL
// ============================================================================

interface CreateTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { id: string; name: string; description: string; base_permission: BasePermission; scopeToOrg: boolean }) => void;
  isSaving: boolean;
  domainName?: string;
  orgName?: string | null;
}

function CreateTreeModal({ isOpen, onClose, onSave, isSaving, domainName, orgName }: CreateTreeModalProps) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [basePermission, setBasePermission] = useState<BasePermission>('organization');
  const [scopeToOrg, setScopeToOrg] = useState<boolean>(!!orgName);

  useEffect(() => {
    if (isOpen) {
      setId(''); setName(''); setDescription(''); setBasePermission('organization');
      setScopeToOrg(!!orgName);
    }
  }, [isOpen, orgName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id.trim() || !name.trim()) return;
    onSave({ id: id.trim(), name: name.trim(), description, base_permission: basePermission, scopeToOrg });
  };

  return (
    <Modal open={isOpen} onClose={onClose} title={`Crear árbol${domainName ? ` en ${domainName}` : ''}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ID (identificador único)</label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.replace(/[^a-z0-9-]/g, ''))}
            className="input w-full font-mono"
            placeholder="ej: alicun-comercial"
            required
          />
          <p className="mt-1 text-xs text-gray-500">Solo minúsculas, números y guiones.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input w-full" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input w-full" rows={2} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ámbito</label>
          <select
            value={scopeToOrg ? 'org' : 'global'}
            onChange={(e) => setScopeToOrg(e.target.value === 'org')}
            className="input w-full"
            disabled={!orgName}
          >
            <option value="global">Global (todas las organizaciones)</option>
            {orgName && <option value="org">Solo «{orgName}»</option>}
          </select>
          {!orgName && (
            <p className="mt-1 text-xs text-gray-500">Para crear un árbol de una organización, selecciónala primero en el selector de organización.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Permiso base</label>
          <select value={basePermission} onChange={(e) => setBasePermission(e.target.value as BasePermission)} className="input w-full">
            <option value="system">Sistema (super_admin)</option>
            <option value="organization">Organización (admins de la org)</option>
            <option value="user">Usuario (cualquier user autenticado)</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Creando…' : 'Crear árbol'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MetadataPage() {
  const { hasPermission } = useAuthStore();
  const { selectedOrg } = useOrganizationStore();

  // Data states
  const [domains, setDomains] = useState<MetadataDomain[]>([]);
  const [trees, setTrees] = useState<MetadataTree[]>([]);
  const [nodes, setNodes] = useState<MetadataNode[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingTrees, setLoadingTrees] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(false);

  // Navigation states
  const [selectedDomain, setSelectedDomain] = useState<MetadataDomain | null>(null);
  const [selectedTree, setSelectedTree] = useState<MetadataTree | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [editingNode, setEditingNode] = useState<MetadataNode | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<MetadataNode | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [parentNode, setParentNode] = useState<MetadataNode | null>(null);
  const [showCreateTreeModal, setShowCreateTreeModal] = useState(false);
  const [permissionsNode, setPermissionsNode] = useState<MetadataNode | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Permissions
  const canReadMetadata = hasPermission('metadata:read') || hasPermission('*');
  const canCreateMetadata = hasPermission('metadata:create') || hasPermission('*');
  // Tree import creates org-scoped trees → SuperAdmin only.
  const canImportTrees = hasPermission('*');

  // Load domains on mount
  useEffect(() => {
    loadDomains();
  }, []);

  // Load trees when domain is selected
  useEffect(() => {
    if (selectedDomain) {
      loadTrees(selectedDomain._id);
    }
  }, [selectedDomain]);

  // Load nodes when tree is selected
  useEffect(() => {
    if (selectedTree) {
      loadNodes(selectedTree._id);
    }
  }, [selectedTree]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await metadataApi.getDomains();
      setDomains(response.data.data || []);
    } catch (error) {
      console.error('Error loading domains:', error);
      toast.error('Error al cargar dominios');
    } finally {
      setLoading(false);
    }
  };

  const loadTrees = async (domainId: string) => {
    try {
      setLoadingTrees(true);
      const response = await metadataApi.getTrees(domainId);
      setTrees(response.data.data || []);
    } catch (error) {
      console.error('Error loading trees:', error);
      toast.error('Error al cargar arboles');
    } finally {
      setLoadingTrees(false);
    }
  };

  const loadNodes = async (treeId: string) => {
    try {
      setLoadingNodes(true);
      const response = await metadataApi.getTree(treeId, selectedOrg?._id);
      setNodes(response.data.data?.nodes || []);
    } catch (error) {
      console.error('Error loading nodes:', error);
      toast.error('Error al cargar nodos');
    } finally {
      setLoadingNodes(false);
    }
  };

  // Handlers
  const handleEditNode = (node: MetadataNode) => {
    setEditingNode(node);
    setShowEditModal(true);
  };

  const handleSaveNode = async (data: { name: string; description: string; code: string }) => {
    if (!editingNode) return;

    try {
      setIsSaving(true);
      await metadataApi.updateNode(editingNode._id, {
        name: data.name,
        description: data.description,
        code: data.code,
      });
      toast.success('Nodo actualizado');
      setShowEditModal(false);
      setEditingNode(null);
      // Reload nodes
      if (selectedTree) {
        loadNodes(selectedTree._id);
      }
    } catch (error) {
      console.error('Error updating node:', error);
      toast.error('Error al actualizar nodo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNode = (node: MetadataNode) => {
    setNodeToDelete(node);
    setShowDeleteDialog(true);
  };

  const confirmDeleteNode = async () => {
    if (!nodeToDelete) return;

    try {
      await metadataApi.deleteNode(nodeToDelete._id);
      toast.success('Nodo eliminado');
      setShowDeleteDialog(false);
      setNodeToDelete(null);
      // Reload nodes
      if (selectedTree) {
        loadNodes(selectedTree._id);
      }
    } catch (error: any) {
      console.error('Error deleting node:', error);
      toast.error(error.response?.data?.error || 'Error al eliminar nodo');
    }
  };

  const handleManagePermissions = (node: MetadataNode) => {
    setPermissionsNode(node);
    setShowPermissionsModal(true);
  };

  const handleAddNode = () => {
    if (!selectedTree) {
      toast.error('Selecciona un arbol primero');
      return;
    }
    setParentNode(null);
    setShowAddModal(true);
  };

  const handleAddChild = (parent: MetadataNode) => {
    if (!selectedTree) return;
    setParentNode(parent);
    setShowAddModal(true);
  };

  const handleSaveNewNode = async (data: {
    id: string;
    name: string;
    description: string;
    code: string;
    base_permission: BasePermission;
  }) => {
    if (!selectedTree) return;
    try {
      setIsSaving(true);
      await metadataApi.createNode({
        id: data.id,
        tree_id: selectedTree._id,
        parent_id: parentNode?._id ?? null,
        name: data.name,
        description: data.description || undefined,
        code: data.code || undefined,
        base_permission: data.base_permission,
      });
      toast.success('Nodo creado');
      setShowAddModal(false);
      setParentNode(null);
      loadNodes(selectedTree._id);
    } catch (error: any) {
      console.error('Error creating node:', error);
      toast.error(error.response?.data?.error || 'Error al crear nodo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateTree = async (data: { id: string; name: string; description: string; base_permission: BasePermission; scopeToOrg: boolean }) => {
    if (!selectedDomain) return;
    if (data.scopeToOrg && !selectedOrg?._id) {
      toast.error('Selecciona una organización para crear un árbol suyo');
      return;
    }
    try {
      setIsSaving(true);
      await metadataApi.createTree({
        id: data.id,
        domain_id: selectedDomain._id,
        name: data.name,
        description: data.description || undefined,
        base_permission: data.base_permission,
        organization_id: data.scopeToOrg ? selectedOrg!._id : null,
      });
      toast.success('Árbol creado');
      setShowCreateTreeModal(false);
      loadTrees(selectedDomain._id);
    } catch (error: any) {
      console.error('Error creating tree:', error);
      toast.error(error.response?.data?.error || 'Error al crear árbol');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter domains based on search
  const filteredDomains = domains.filter(
    (domain) =>
      domain.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      domain.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ============================================================================
  // RENDER: LOADING
  // ============================================================================

  if (loading) {
    return <PageLoader />;
  }

  // ============================================================================
  // RENDER: NO PERMISSION
  // ============================================================================

  if (!canReadMetadata) {
    return (
      <div>
        <Header
          title="Metadatos"
          subtitle="Repositorio de taxonomias"
        />
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-yellow-800">Sin acceso</h3>
            <p className="text-yellow-600 mt-2">
              No tienes permisos para ver los metadatos del sistema.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: DOMAIN LIST (MAIN VIEW)
  // ============================================================================

  if (!selectedDomain) {
    return (
      <div>
        <Header
          title="Metadatos"
          subtitle="Repositorio de taxonomias - Fuente de verdad del sistema"
        />

        <div className="p-6 space-y-6">
          {/* Search + import */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative max-w-md flex-1 min-w-[220px]">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar dominio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vida-500 focus:border-vida-500"
              />
            </div>
            {canImportTrees && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                className="btn btn-primary inline-flex items-center gap-2 whitespace-nowrap"
              >
                <ArrowUpTrayIcon className="w-4 h-4" /> Importar árbol
              </button>
            )}
          </div>

          {/* Permission legend */}
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="text-gray-500">Leyenda:</span>
            <div className="flex items-center gap-1">
              <PermissionBadge permission="system" />
              <span className="text-gray-400">Solo lectura</span>
            </div>
            <div className="flex items-center gap-1">
              <PermissionBadge permission="organization" />
              <span className="text-gray-400">Editable por org</span>
            </div>
            <div className="flex items-center gap-1">
              <PermissionBadge permission="user" />
              <span className="text-gray-400">Editable por usuario</span>
            </div>
          </div>

          {/* Domain cards grid */}
          {filteredDomains.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchQuery
                ? `No se encontraron dominios que coincidan con "${searchQuery}"`
                : 'No hay dominios disponibles'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDomains.map((domain) => (
                <DomainCard
                  key={domain._id}
                  domain={domain}
                  onClick={() => setSelectedDomain(domain)}
                />
              ))}
            </div>
          )}
        </div>

        {showImportModal && (
          <ImportTreeModal
            onClose={() => setShowImportModal(false)}
            onImported={loadDomains}
          />
        )}
      </div>
    );
  }

  // ============================================================================
  // RENDER: DOMAIN DETAIL WITH TREES
  // ============================================================================

  return (
    <div>
      <Header
        title={selectedDomain.name}
        subtitle={selectedDomain.description}
      />

      <div className="p-6 space-y-6">
        {/* Back button */}
        <button
          onClick={() => {
            setSelectedDomain(null);
            setSelectedTree(null);
            setTrees([]);
            setNodes([]);
          }}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-vida-600 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Volver a dominios
        </button>

        {loadingTrees ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vida-600" />
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Sidebar with trees list */}
            <div className="w-64 flex-shrink-0">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">Arboles</h3>
                  {canCreateMetadata && (
                    <button
                      onClick={() => setShowCreateTreeModal(true)}
                      className="p-1 text-vida-600 hover:bg-vida-50 rounded"
                      title="Crear árbol"
                    >
                      <PlusIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="p-2">
                  {trees.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No hay arboles
                    </p>
                  ) : (
                    trees.map((tree) => (
                      <button
                        key={tree._id}
                        onClick={() => setSelectedTree(tree)}
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                          selectedTree?._id === tree._id
                            ? 'bg-vida-50 text-vida-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{tree.name}</span>
                        <PermissionBadge permission={tree.base_permission} />
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Permission legend */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <PermissionBadge permission="system" />
                  <span className="text-gray-500">Solo lectura</span>
                </div>
                <div className="flex items-center gap-2">
                  <PermissionBadge permission="organization" />
                  <span className="text-gray-500">Editable</span>
                </div>
                <div className="flex items-center gap-2">
                  <PermissionBadge permission="user" />
                  <span className="text-gray-500">Personalizable</span>
                </div>
              </div>
            </div>

            {/* Tree content */}
            <div className="flex-1">
              {loadingNodes ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vida-600" />
                </div>
              ) : selectedTree ? (
                <TreeView
                  tree={selectedTree}
                  nodes={nodes}
                  onEditNode={handleEditNode}
                  onDeleteNode={handleDeleteNode}
                  onManagePermissions={handleManagePermissions}
                  onAddNode={handleAddNode}
                  onAddChild={handleAddChild}
                  canAddNodes={canCreateMetadata}
                />
              ) : (
                <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
                  <p className="text-gray-500">
                    Selecciona un arbol del panel izquierdo para ver su contenido
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditNodeModal
        node={editingNode}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingNode(null);
        }}
        onSave={handleSaveNode}
        isSaving={isSaving}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setNodeToDelete(null);
        }}
        onConfirm={confirmDeleteNode}
        title="Eliminar nodo"
        message={`¿Estas seguro de eliminar el nodo "${nodeToDelete?.name}"? Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      {/* Add Node Modal */}
      <AddNodeModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setParentNode(null); }}
        onSave={handleSaveNewNode}
        isSaving={isSaving}
        treeName={selectedTree?.name}
        parentName={parentNode?.name ?? null}
      />

      {/* Create Tree Modal */}
      <CreateTreeModal
        isOpen={showCreateTreeModal}
        onClose={() => setShowCreateTreeModal(false)}
        onSave={handleCreateTree}
        isSaving={isSaving}
        domainName={selectedDomain?.name}
        orgName={selectedOrg?.name ?? null}
      />

      {/* Manage Permissions Modal */}
      <ManagePermissionsModal
        node={permissionsNode}
        isOpen={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setPermissionsNode(null);
        }}
      />
    </div>
  );
}
